'use client'

import { motion } from 'motion/react'
import { useInView } from 'react-intersection-observer'
import AnimatedText from '@/components/Antonie/Components/animated-text'
import WeatherCard, { WeatherCardData } from './WeatherCard'

interface WeatherClientProps {
  data: any // Use any for now, as Weather type is not available
}

export const WeatherClient: React.FC<WeatherClientProps> = ({ data }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  // Example mapping: adapt this to your real data structure
  const weatherCards: WeatherCardData[] = [
    {
      title: 'Vormittag',
      temperature: '8°C',
      sunshineChance: '40%',
      frostLevel: '2800m',
      icon: '⛅️',
    },
    {
      title: 'Nachmittag',
      temperature: '14°C',
      sunshineChance: '20%',
      frostLevel: '3000m',
      icon: '⛈️',
    },
    {
      title: 'Donnerstag',
      temperature: '8°C',
      sunshineChance: '10%',
      frostLevel: '2500m',
      icon: '🌧️',
    },
    {
      title: 'Freitag',
      temperature: '11°C',
      sunshineChance: '40%',
      frostLevel: '2700m',
      icon: '🌦️',
    },
  ]

  // Example forecast and trend text
  const forecast =
    'Ab heute nimmt der Tiefdruckeinfluss zu: Am ehesten beschert uns der Vormittag noch Auflockerungen und trockene Wetterphasen, am Nachmittag überwiegt dann die Schauer- und Gewittertätigkeit.'
  const trend =
    'Am Donnerstag geht es mit wolkenverhangenem Himmel und Regenwetter weiter. Der Freitag und Samstag bringen Schauer mit etwas Sonne. Nachhaltige Besserung wird für den Sonntag erwartet.'

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  return (
    <section className="py-12 md:py-24">
      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="mx-auto max-w-5xl bg-white p-4 shadow-xl md:p-8"
      >
        <motion.div variants={itemVariants} className="mb-12 text-center">
          <AnimatedText
            text={data.title}
            type="h2"
            className="mb-6 font-heading text-3xl text-antonie-brown md:text-4xl"
          />
          <p className="text-lg font-light text-gray-600">{data.subtitle}</p>
        </motion.div>

        {/* Weather cards grid */}
        <motion.div
          variants={itemVariants}
          className="mb-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4"
        >
          {weatherCards.map((card, idx) => (
            <WeatherCard key={card.title + idx} data={card} />
          ))}
        </motion.div>

        {/* Forecast and trend section */}
        <motion.div variants={itemVariants} className="mt-8 space-y-6">
          <div>
            <span className="font-semibold text-gray-800">Vorhersage:</span>
            <p className="mt-1 text-gray-700">{forecast}</p>
          </div>
          <div>
            <span className="font-semibold text-gray-800">Tendenz:</span>
            <p className="mt-1 text-gray-700">{trend}</p>
          </div>
        </motion.div>
      </motion.div>
    </section>
  )
}
